import { Loader } from '@mantine/core';

import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { Pager } from './Pager';
import type { PageQuery, PagerHandle } from './pageState';

/**
 * Три состояния экрана с запросом до того, как показывать данные: отказ, ожидание и пустая
 * страница (AD-A11). Четыре экрана собирали эти три строки дословно одинаково, а ещё три —
 * каждый по-своему; теперь их собирает одно место.
 */
export function PageFallback({
  query,
  empty,
  loaderSize,
}: {
  query: PageQuery;
  empty: string;
  loaderSize?: string;
}) {
  if (query.isError) return <ErrorState error={query.error} />;
  if (query.isPending) return <Loader size={loaderSize} />;
  if (query.data && query.data.items.length === 0) return <EmptyState>{empty}</EmptyState>;
  return null;
}

/**
 * «Назад / дальше» для прочитанной страницы (AD-A8): те же четыре экрана повторяли одну и ту
 * же обвязку вокруг `<Pager>`. Стек курсоров живёт в `useCursorPager`, а решение «можно ли
 * дальше» всегда одно и читается из `nextCursor`.
 */
export function CursorPager({ query, pager }: { query: PageQuery; pager: PagerHandle }) {
  const next = query.data?.nextCursor;
  if (!query.data) return null;

  return (
    <Pager
      canGoBack={pager.canGoBack}
      canGoForward={next !== null && next !== undefined}
      onBack={pager.back}
      onForward={() => {
        if (next) pager.forward(next);
      }}
      loading={query.isFetching ?? false}
    />
  );
}
